import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { 
  Search, 
  Car, 
  Filter,
  Phone,
  Globe,
  ChevronRight,
  Loader2,
  X,
  Clock,
  Shield,
  Truck,
  MessageCircle,
  DollarSign,
  Users,
  Award,
  CheckCircle,
  Star,
  Sparkles,
  Building2,
  Send
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pre-filled WhatsApp message
const WHATSAPP_DEFAULT_MESSAGE = "Hola, vi la página de maquinaria y quisiera más información.";

// Business configurations
const BUSINESS_CONFIG = {
  automotriz: {
    id: "automotriz",
    name: "HEBEH Automotriz",
    slug: "automotriz",
    heroTitle: "Tu próximo vehículo te espera",
    heroSubtitle: "Descubre nuestra selección de vehículos nuevos con financiamiento a tu medida",
    icon: Car,
    accentColor: "#3B82F6",
    accentColorSecondary: "#F09A21",
    gradientClass: "bg-gradient-to-r from-blue-600 via-blue-500 to-orange-500",
    whatsapp: "5513181745",
    phone: "55 1318 1745",
    itemLabel: "vehículo",
    itemLabelPlural: "vehículos",
    website: "automotriz.hebeh.com.mx"
  },
  maquinaria: {
    id: "maquinaria",
    name: "HEBEH Maquinaria",
    slug: "maquinaria",
    heroTitle: "POTENCIA PARA TU PROYECTO",
    heroSubtitle: "Maquinaria de construcción y transporte con las mejores condiciones de financiamiento",
    icon: Building2,
    // Colores del manual de identidad HEBEH
    accentColor: "#F09A21", // Acento Cálido HEBEH
    accentColorSecondary: "#1E4C7A", // Azul Profundo Corporativo
    primaryDark: "#0B1623", // Azul Oscuro HEBEH (Master)
    gradientClass: "bg-gradient-to-r from-[#0B1623] via-[#1E4C7A] to-[#F09A21]",
    whatsapp: "5513181745",
    phone: "55 1318 1745",
    itemLabel: "equipo",
    itemLabelPlural: "equipos",
    // Logo oficial
    logoUrl: "https://customer-assets.emergentagent.com/job_5eca9e74-1690-4426-bde8-88577910a114/artifacts/zt71l0ae_LOGO%20OFICIAL%20BLANCO%20HEBEH.png",
    // Imágenes para carrusel hero - Maquinaria en obras de construcción
    heroImages: [
      "https://images.unsplash.com/photo-1763624578810-5ac518e4fd4c?w=1920&q=85", // Excavadora amarilla en obra
      "https://images.unsplash.com/photo-1748848436533-f7583dce22b6?w=1920&q=85", // Excavadora en terreno arenoso (vista aérea)
      "https://images.unsplash.com/photo-1766595680974-e63877a2ab5b?w=1920&q=85", // Excavadora amarilla trabajando
      "https://images.unsplash.com/photo-1664312616511-81fe2e745cb3?w=1920&q=85"  // Retroexcavadora en obra
    ]
  }
};

// Helper to get business from URL params or hostname
const getBusinessFromContext = (slug) => {
  // Check URL param first
  if (slug && BUSINESS_CONFIG[slug]) {
    return BUSINESS_CONFIG[slug];
  }
  
  // Check hostname for production subdomains
  const hostname = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  if (hostname.includes("maquinaria")) {
    return BUSINESS_CONFIG.maquinaria;
  }
  // Subdominio "automotriz.*" (ej. automotriz.hebeh.com.mx) → automotriz
  if (hostname === "automotriz" || hostname.startsWith("automotriz.") || hostname.startsWith("automotriz-")) {
    return BUSINESS_CONFIG.automotriz;
  }
  
  // Default to automotriz
  return BUSINESS_CONFIG.automotriz;
};

// WhatsApp floating button component with pre-filled message
const WhatsAppButton = ({ business }) => {
  const config = business || BUSINESS_CONFIG.automotriz;
  const message = config.id === "maquinaria" 
    ? WHATSAPP_DEFAULT_MESSAGE 
    : `Hola, quisiera información sobre un ${config.itemLabel || "vehículo"}`;
  
  return (
    <a
      href={`https://api.whatsapp.com/send?phone=${config.whatsapp}&text=${encodeURIComponent(message)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="w-14 h-14 flex items-center justify-center rounded-full bg-[#25D366] shadow-lg hover:scale-110 transition-transform"
      data-testid="whatsapp-float-btn"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Promotional Banner Component
const PromoBanner = ({ banner, featuredCount, onScrollToFeatured, business }) => {
  if (!banner?.enabled) return null;
  
  const config = business || BUSINESS_CONFIG.automotriz;
  const itemLabel = featuredCount === 1 ? config.itemLabel : config.itemLabelPlural;

  const bgStyles = {
    gradient: config.gradientClass || "bg-gradient-to-r from-blue-600 via-blue-500 to-orange-500",
    blue: "bg-blue-600",
    orange: "bg-orange-500",
    dark: "bg-slate-800"
  };

  return (
    <div 
      className={`relative overflow-hidden rounded-2xl ${bgStyles[banner.background_color] || bgStyles.gradient} p-8 md:p-12 mb-10`}
      data-testid="promo-banner"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      
      <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-yellow-300" />
            <span className="text-sm font-medium text-white/90 uppercase tracking-wide">
              {featuredCount > 0 ? `${featuredCount} ${itemLabel} destacados` : "Ofertas especiales"}
            </span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-2">
            {banner.title}
          </h2>
          <p className="text-white/80 text-lg max-w-xl">
            {banner.subtitle}
          </p>
        </div>
        
        {featuredCount > 0 && (
          <button
            onClick={onScrollToFeatured}
            className="flex items-center gap-2 px-6 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors shadow-lg"
            data-testid="banner-cta-btn"
          >
            <Star className="w-5 h-5 text-orange-500" />
            {banner.cta_text || "Ver Ofertas"}
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

// Featured Vehicle Card (special styling)
const FeaturedVehicleCard = ({ vehicle, business }) => {
  const config = business || BUSINESS_CONFIG.automotriz;
  
  const formatPrice = (price) => {
    if (!price) return "Consultar";
    const numPrice = parseFloat(price.replace(/[,$]/g, ""));
    return `$${numPrice.toLocaleString("es-MX")}`;
  };

  // Prioridad: web_image > Hook de gallery > vehicle_image
  const getDisplayImage = () => {
    if (vehicle.web_image) return vehicle.web_image;
    if (vehicle.gallery_images && vehicle.gallery_images.length > 0) {
      const hookImage = vehicle.gallery_images.find(img => img.name === "Hook");
      if (hookImage?.url) return hookImage.url;
      return vehicle.gallery_images[0]?.url || null;
    }
    return vehicle.vehicle_image || null;
  };

  const displayImage = getDisplayImage();

  return (
    <Link 
      to={`/catalogo/${config.slug}/${vehicle.id}`}
      className="relative glass-card overflow-hidden group transition-all duration-300 ring-2"
      style={{ borderColor: `${config.accentColor}30`, ringColor: `${config.accentColor}30` }}
      data-testid={`featured-card-${vehicle.id}`}
    >
      {/* Featured Badge */}
      <div 
        className="absolute top-3 left-3 z-10 flex items-center gap-1 px-2 py-1 text-white text-xs font-semibold rounded-full"
        style={{ backgroundColor: config.accentColor }}
      >
        <Star className="w-3 h-3" />
        Destacado
      </div>

      {/* Vehicle Image */}
      <div className="aspect-[4/3] bg-slate-800 overflow-hidden">
        {displayImage ? (
          <img 
            src={displayImage} 
            alt={`${vehicle.brand} ${vehicle.model_name}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-700 to-slate-800">
            <Car className="w-16 h-16 text-slate-600" />
          </div>
        )}
      </div>
      
      {/* Vehicle Info */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-orange-400 font-medium uppercase tracking-wide">
              {vehicle.brand || "Vehículo"}
            </p>
            <h3 className="text-lg font-bold text-white mt-1">
              {vehicle.model_name}
            </h3>
            <p className="text-sm text-slate-400">
              {vehicle.version} {vehicle.year && `• ${vehicle.year}`}
            </p>
          </div>
        </div>
        
        {/* Price */}
        <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">Precio</p>
            <p className="text-xl font-bold text-white">{formatPrice(vehicle.price)}</p>
          </div>
          <div className="flex items-center gap-1 text-orange-400 text-sm font-medium group-hover:gap-2 transition-all">
            Ver más
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
};

// Stats counter component
const StatCard = ({ icon: Icon, value, label, suffix = "" }) => (
  <div className="glass-card p-6 text-center">
    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/20 flex items-center justify-center">
      <Icon className="w-6 h-6 text-blue-400" />
    </div>
    <p className="text-3xl font-bold text-white">{value}{suffix}</p>
    <p className="text-slate-400 mt-1 text-sm">{label}</p>
  </div>
);

// Guarantee card component
const GuaranteeCard = ({ icon: Icon, title, description }) => (
  <div className="glass-card p-5 text-center">
    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-orange-500/20 flex items-center justify-center">
      <Icon className="w-6 h-6 text-orange-400" />
    </div>
    <p className="font-semibold text-white">{title}</p>
    <p className="text-sm text-slate-400 mt-1">{description}</p>
  </div>
);

// Step card component
const StepCard = ({ number, title, description }) => (
  <div className="glass-card p-6">
    <div className="flex items-start gap-4">
      <div className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-blue-500 text-blue-400 font-bold flex-shrink-0">
        {number}
      </div>
      <div>
        <p className="text-lg font-semibold text-white">{title}</p>
        <p className="text-slate-400 mt-1">{description}</p>
      </div>
    </div>
  </div>
);

// Scroll to top button
const ScrollToTopButton = () => {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 text-white shadow-lg hover:bg-slate-700 transition-all flex items-center justify-center"
      aria-label="Ir al inicio"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    </button>
  );
};

// Chatbot button for Maquinaria - Ahora junto al WhatsApp
const ChatbotButton = ({ business }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [messages, setMessages] = React.useState([
    { role: 'assistant', content: '¡Hola! Soy el asistente de HEBEH Maquinaria. ¿En qué puedo ayudarte hoy?' }
  ]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chatbot/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          business_id: business.id
        })
      });
      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response || 'Lo siento, no pude procesar tu consulta.' }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Lo siento, hubo un error. Por favor contacta por WhatsApp.' }]);
    } finally {
      setLoading(false);
    }
  };

  // Only show for maquinaria
  if (business.id !== 'maquinaria') return null;

  return (
    <>
      {/* Chat button - ya no tiene fixed, se incluye en el grupo */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 rounded-full bg-[#1E4C7A] text-white shadow-lg hover:bg-[#2a5f94] transition-all flex items-center justify-center"
        aria-label="Abrir chat"
        data-testid="chatbot-btn"
      >
        {isOpen ? (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )}
      </button>

      {/* Chat window - posicionado a la derecha */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-80 md:w-96 bg-[#0B1623] border border-slate-700 rounded-2xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-[#1E4C7A] p-4">
            <h4 className="text-white font-semibold">Asistente HEBEH</h4>
            <p className="text-blue-200 text-sm">Pregúntame sobre maquinaria</p>
          </div>

          {/* Messages */}
          <div className="h-72 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-[#1E4C7A] text-white'
                      : 'bg-slate-800 text-slate-200'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 text-slate-400 px-4 py-2 rounded-2xl text-sm">
                  Escribiendo...
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-700">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Escribe tu pregunta..."
                className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-[#1E4C7A]"
              />
              <button
                onClick={sendMessage}
                disabled={loading}
                className="px-4 py-2 bg-[#1E4C7A] text-white rounded-xl hover:bg-[#2a5f94] transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Hero Carousel Component for Maquinaria
const HeroCarousel = ({ business }) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const images = business.heroImages || [];
  
  React.useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  if (images.length === 0) return null;

  return (
    <section className="relative mb-16" data-testid="hero-carousel">
      {/* Carousel Images - Full Width, más alto */}
      <div className="relative h-[500px] md:h-[650px] w-full">
        {images.map((img, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              idx === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img 
              src={img} 
              alt={`Maquinaria ${idx + 1}`}
              className="w-full h-full object-cover object-center"
            />
            {/* Gradient overlay - solo desde abajo para no oscurecer arriba */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1623] via-transparent to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B1623]/80 via-[#0B1623]/30 to-transparent" />
          </div>
        ))}
        
        {/* Content overlay */}
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-2xl">
              <h1 className="font-montserrat text-4xl md:text-6xl font-bold text-white mb-4 tracking-tight">
                {business.heroTitle}
              </h1>
              <p className="text-lg md:text-xl text-slate-300 mb-8">
                {business.heroSubtitle}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={`https://api.whatsapp.com/send?phone=${business.whatsapp}&text=${encodeURIComponent(WHATSAPP_DEFAULT_MESSAGE)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 rounded-lg bg-[#25D366] text-white font-semibold hover:bg-[#20BD5C] transition-colors flex items-center gap-2"
                  data-testid="hero-whatsapp-btn"
                >
                  <MessageCircle className="w-5 h-5" />
                  Cotizar Ahora
                </a>
                <button
                  onClick={() => document.getElementById('catalog-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-6 py-3 rounded-lg bg-[#1E4C7A] text-white font-semibold hover:bg-[#2a5f94] transition-colors"
                >
                  Ver Catálogo
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Carousel indicators */}
        {images.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentSlide 
                    ? 'w-8 bg-white' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Ir a slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Why Choose Us Section Component - Elegant Design
const WhyChooseUsSection = ({ business }) => {
  const features = [
    {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Equipos Certificados",
      description: "Maquinaria inspeccionada y garantizada"
    },
    {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Financiamiento Flexible",
      description: "Planes a tu medida con las mejores tasas"
    },
    {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Envío Nacional",
      description: "Entregamos en toda la República"
    },
    {
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      title: "Soporte Técnico",
      description: "Asesoría experta pre y post venta"
    }
  ];

  return (
    <section className="mb-16 py-16 -mx-6 px-6" data-testid="why-choose-us">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            ¿Por qué elegir HEBEH?
          </h2>
          {/* Separador mitad azul, mitad naranja */}
          <div className="flex justify-center gap-0">
            <div className="w-10 h-1 rounded-l-full" style={{ backgroundColor: '#1E4C7A' }} />
            <div className="w-10 h-1 rounded-r-full" style={{ backgroundColor: '#F09A21' }} />
          </div>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto text-lg">
            Más de 15 años de experiencia respaldando a constructores y empresarios
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {features.map((feature, idx) => (
            <div 
              key={idx}
              className="group text-center p-6 rounded-2xl bg-gradient-to-b from-slate-800/50 to-transparent border border-slate-700/50 hover:border-[#F09A21]/30 transition-all duration-300"
            >
              <div 
                className="w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110"
                style={{ 
                  background: `linear-gradient(135deg, ${business.accentColor}15, ${business.accentColor}05)`,
                  color: business.accentColor
                }}
              >
                {feature.icon}
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">{feature.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Brands Section Component for Maquinaria - With Real Logos
const BrandsSection = ({ business }) => {
  const brands = [
    { 
      name: "Caterpillar", 
      logoUrl: "https://static.prod-images.emergentagent.com/jobs/0c33a225-16da-44cd-a956-92be24d05c7e/images/5a98a106d1f6f850662fb52a3f6100fa8389fcfe4f28f9211a2fa4007f6c63fc.png"
    },
    { 
      name: "Komatsu", 
      logoUrl: "https://static.prod-images.emergentagent.com/jobs/0c33a225-16da-44cd-a956-92be24d05c7e/images/3ae7804f6fc0c686f269e46b1c382c74bf69ee34d5876ff93f8cbb237c5a8fdd.png"
    },
    { 
      name: "John Deere", 
      logoUrl: "https://static.prod-images.emergentagent.com/jobs/0c33a225-16da-44cd-a956-92be24d05c7e/images/f0f4221b4631bcd85bdead6b2bf11f82b220a8341b0dea2a172f8ed2ab401a01.png"
    },
    { 
      name: "Case", 
      logoUrl: "https://static.prod-images.emergentagent.com/jobs/0c33a225-16da-44cd-a956-92be24d05c7e/images/fb315691dc800291789c36018df4b640ea1531f4a4ffd484f50b141214edc584.png"
    },
    { 
      name: "Volvo", 
      logoUrl: "https://static.prod-images.emergentagent.com/jobs/0c33a225-16da-44cd-a956-92be24d05c7e/images/a3f4b57ac9ca686baee78b76c337dfdd91bf9ef166b20abf516b7aeb53e57f4c.png"
    },
    { 
      name: "Hyundai", 
      logoUrl: "https://static.prod-images.emergentagent.com/jobs/0c33a225-16da-44cd-a956-92be24d05c7e/images/e404b5380a8c1541f46ec5a9b47a56aedf3f4edb2ee24e35713ce8499eb13300.png"
    },
    { 
      name: "JCB", 
      logoUrl: "https://static.prod-images.emergentagent.com/jobs/0c33a225-16da-44cd-a956-92be24d05c7e/images/f96f7e20f22cf64fa69ed937ce4075bec5c5c6fc619bc5875e85c6e45866cbee.png"
    },
    { 
      name: "Bobcat", 
      logoUrl: "https://static.prod-images.emergentagent.com/jobs/0c33a225-16da-44cd-a956-92be24d05c7e/images/56aa81a3802162c291f2932e3ff2945fab357bb19b523a2fb11f095acb0f2ada.png"
    }
  ];

  return (
    <section className="mb-16 py-12" data-testid="brands-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Marcas que Manejamos
          </h2>
          {/* Separador mitad azul, mitad naranja */}
          <div className="flex justify-center gap-0">
            <div className="w-10 h-1 rounded-l-full" style={{ backgroundColor: '#1E4C7A' }} />
            <div className="w-10 h-1 rounded-r-full" style={{ backgroundColor: '#F09A21' }} />
          </div>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">
            Trabajamos con las marcas líderes en maquinaria de construcción
          </p>
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3 md:gap-4">
          {brands.map((brand, idx) => (
            <div 
              key={idx}
              className="group aspect-square flex items-center justify-center p-2 md:p-3 rounded-xl bg-white border border-slate-200 hover:shadow-lg hover:shadow-[#F09A21]/20 transition-all duration-300"
              title={brand.name}
            >
              <img 
                src={brand.logoUrl} 
                alt={brand.name}
                className="max-w-full max-h-full object-contain transition-transform group-hover:scale-105"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Equipment Categories Section for Maquinaria - Uses web images from actual vehicles
const EquipmentCategoriesSection = ({ business, onCategoryClick, vehicles = [] }) => {
  // Categorías con vehículo representativo de la base de datos
  const getCategoryImage = (filterKey, defaultImage) => {
    // Buscar un vehículo que coincida con la categoría y tenga web_image
    const matchingVehicle = vehicles.find(v => {
      const modelName = (v.model_name || "").toLowerCase();
      const brand = (v.brand || "").toLowerCase();
      const fullName = `${modelName} ${brand}`;
      
      if (filterKey === "retroexcavadora") {
        return fullName.includes("retroexcavadora") || fullName.includes("420f");
      } else if (filterKey === "excavadora") {
        return (fullName.includes("excavadora") || fullName.includes("336")) && !fullName.includes("retro");
      } else if (filterKey === "camion") {
        return fullName.includes("camion") || fullName.includes("camión") || fullName.includes("international");
      } else if (filterKey === "cargador") {
        return fullName.includes("cargador") || fullName.includes("966");
      }
      return false;
    });
    
    // Usar la web_image del vehículo si existe, sino usar la default
    if (matchingVehicle && matchingVehicle.web_image && matchingVehicle.web_image.startsWith("data:image")) {
      return matchingVehicle.web_image;
    }
    return defaultImage;
  };
  
  const categories = [
    {
      name: "Retroexcavadoras",
      filterKey: "retroexcavadora",
      image: getCategoryImage("retroexcavadora", "https://images.pexels.com/photos/29503749/pexels-photo-29503749.jpeg?auto=compress&w=600"),
      description: "Versátiles para excavación y carga"
    },
    {
      name: "Excavadoras",
      filterKey: "excavadora",
      image: getCategoryImage("excavadora", "https://images.unsplash.com/photo-1763624578810-5ac518e4fd4c?w=600&q=80"),
      description: "Para trabajos pesados de excavación"
    },
    {
      name: "Camiones",
      filterKey: "camion",
      image: getCategoryImage("camion", "https://images.unsplash.com/photo-1763349148643-9b21e1cefe50?w=600&q=80"),
      description: "Transporte de carga y materiales"
    },
    {
      name: "Cargadores",
      filterKey: "cargador",
      image: getCategoryImage("cargador", "https://images.unsplash.com/photo-1762608674744-b3eb7be7c30b?w=600&q=80"),
      description: "Para movimiento de tierra y materiales"
    }
  ];

  const handleCategoryClick = (category) => {
    if (onCategoryClick) {
      onCategoryClick(category.filterKey);
    }
    // Scroll to catalog section
    document.getElementById('catalog-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="mb-16 py-12" data-testid="categories-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Tipos de Equipo
          </h2>
          {/* Separador mitad azul, mitad naranja */}
          <div className="flex justify-center gap-0">
            <div className="w-10 h-1 rounded-l-full" style={{ backgroundColor: '#1E4C7A' }} />
            <div className="w-10 h-1 rounded-r-full" style={{ backgroundColor: '#F09A21' }} />
          </div>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">
            Encuentra el equipo ideal para tu proyecto
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {categories.map((category, idx) => (
            <button
              key={idx}
              onClick={() => handleCategoryClick(category)}
              className="group relative overflow-hidden rounded-2xl aspect-[4/5] text-left"
              data-testid={`category-${category.filterKey}`}
            >
              <img 
                src={category.image} 
                alt={category.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1623] via-[#0B1623]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <h3 className="text-xl font-bold text-white mb-1">{category.name}</h3>
                <p className="text-sm text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {category.description}
                </p>
              </div>
              <div 
                className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
                style={{ backgroundColor: business.accentColor }}
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quote Form Section for Maquinaria
const QuoteFormSection = ({ business }) => {
  const [formData, setFormData] = React.useState({
    name: '',
    phone: '',
    email: '',
    equipment: '',
    message: ''
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Submit to backend
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          business_id: business.id,
          source: 'catalog_form'
        })
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="mb-16 py-16" data-testid="quote-form-section">
        {/* Fondo full-width con gradientes suaves en ambos lados */}
        <div className="absolute left-0 right-0 h-full bg-gradient-to-r from-transparent via-[#1E4C7A]/15 to-transparent -z-10" style={{ marginTop: '-64px', height: 'calc(100% + 128px)' }} />
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${business.accentColor}20` }}>
            <svg className="w-10 h-10" style={{ color: business.accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-white mb-3">¡Gracias por tu interés!</h3>
          <p className="text-slate-400 mb-6">
            Hemos recibido tu solicitud. Un asesor se pondrá en contacto contigo a la brevedad.
          </p>
          <a
            href={`https://api.whatsapp.com/send?phone=${business.whatsapp}&text=${encodeURIComponent(WHATSAPP_DEFAULT_MESSAGE)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#25D366] text-white font-semibold hover:bg-[#20BD5C] transition-colors"
          >
            <MessageCircle className="w-5 h-5" />
            Contactar por WhatsApp
          </a>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-b from-[#0B1623] via-[#0f1d2c] to-[#0B1623]" data-testid="quote-form-section">
      {/* Overlay de gradiente horizontal sutil */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to right, transparent, rgba(30, 76, 122, 0.1), transparent)'
        }}
      />
      
      <div className="relative max-w-4xl mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Cotiza tu Equipo
          </h2>
          {/* Separador mitad azul, mitad naranja */}
          <div className="flex justify-center gap-0">
            <div className="w-10 h-1 rounded-l-full" style={{ backgroundColor: '#1E4C7A' }} />
            <div className="w-10 h-1 rounded-r-full" style={{ backgroundColor: '#F09A21' }} />
          </div>
          <p className="text-slate-400 mt-4 max-w-xl mx-auto">
            Déjanos tus datos y un asesor especializado te contactará
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Nombre completo *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 rounded-lg bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-[#F09A21] transition-colors"
              placeholder="Tu nombre"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Teléfono / WhatsApp *</label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-4 py-3 rounded-lg bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-[#F09A21] transition-colors"
              placeholder="55 1234 5678"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Correo electrónico</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-3 rounded-lg bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-[#F09A21] transition-colors"
              placeholder="tu@email.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Tipo de equipo de interés</label>
            <select
              value={formData.equipment}
              onChange={(e) => setFormData({...formData, equipment: e.target.value})}
              className="w-full px-4 py-3 rounded-lg bg-slate-800/50 border border-slate-700 text-white focus:outline-none focus:border-[#F09A21] transition-colors"
            >
              <option value="">Selecciona una opción</option>
              <option value="retroexcavadora">Retroexcavadora</option>
              <option value="excavadora">Excavadora</option>
              <option value="cargador">Cargador</option>
              <option value="camion">Camión</option>
              <option value="otro">Otro equipo</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-300 mb-2">Mensaje o especificaciones</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              rows={4}
              className="w-full px-4 py-3 rounded-lg bg-slate-800/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-[#F09A21] transition-colors resize-none"
              placeholder="Cuéntanos más sobre lo que necesitas..."
            />
          </div>
          
          <div className="md:col-span-2 flex flex-col sm:flex-row items-center gap-4">
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto px-8 py-3 rounded-lg bg-[#1E4C7A] text-white font-semibold transition-all hover:bg-[#2a5f94] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Solicitar Cotización
                </>
              )}
            </button>
            <span className="text-slate-500 text-sm">
              O contáctanos directamente por{' '}
              <a 
                href={`https://api.whatsapp.com/send?phone=${business.whatsapp}&text=${encodeURIComponent(WHATSAPP_DEFAULT_MESSAGE)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#25D366] hover:underline"
              >
                WhatsApp
              </a>
            </span>
          </div>
        </form>
      </div>
    </section>
  );
};

// Vehicle card component
const VehicleCard = ({ vehicle, business, isRental = false }) => {
  const config = business || BUSINESS_CONFIG.automotriz;
  const BusinessIcon = config.icon || Car;
  const isGrouped = !!vehicle._grouped;
  const versionsCount = vehicle._versionsCount || 1;
  
  const formatPrice = (price) => {
    if (!price) return "Consultar";
    const numPrice = parseFloat(price.toString().replace(/[,$]/g, ""));
    return `$${numPrice.toLocaleString("es-MX")}`;
  };

  // Prioridad: web_image > primera imagen de gallery_images > vehicle_image
  const getDisplayImage = () => {
    if (vehicle.web_image) return vehicle.web_image;
    if (vehicle.gallery_images && vehicle.gallery_images.length > 0) {
      const hookImage = vehicle.gallery_images.find(img => img.name === "Hook");
      if (hookImage?.url) return hookImage.url;
      return vehicle.gallery_images[0]?.url || null;
    }
    return vehicle.vehicle_image || null;
  };

  const displayImage = getDisplayImage();
  
  // For rental vehicles, use rental_price
  const displayPrice = isRental ? vehicle.rental_price : vehicle.price;
  const priceLabel = isRental ? "Renta Mensual" : (isGrouped ? "Precio desde" : "Precio");
  const priceDisplay = isRental 
    ? (displayPrice ? `$${parseInt(displayPrice).toLocaleString('es-MX')}/mes` : "Consultar")
    : formatPrice(displayPrice);

  return (
    <Link 
      to={`/catalogo/${config.slug}/${vehicle.id}${isRental ? '?type=rental' : ''}`}
      className="glass-card overflow-hidden group transition-all duration-300 relative"
      style={{ ':hover': { borderColor: `${config.accentColor}50` } }}
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      {/* Rental Badge */}
      {isRental && (
        <div className="absolute top-3 right-3 z-10 px-2 py-1 bg-[#F09A21] text-white text-xs font-bold rounded">
          RENTA
        </div>
      )}
      
      {/* Versions Badge */}
      {isGrouped && versionsCount > 1 && (
        <div
          className="absolute top-3 right-3 z-10 px-2 py-1 text-white text-xs font-bold rounded"
          style={{ backgroundColor: config.accentColor }}
          data-testid={`versions-badge-${vehicle.id}`}
        >
          {versionsCount} versiones
        </div>
      )}
      
      {/* Vehicle Image */}
      <div className="aspect-[4/3] bg-slate-800 overflow-hidden">
        {displayImage ? (
          <img 
            src={displayImage} 
            alt={`${vehicle.brand} ${vehicle.model_name}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BusinessIcon className="w-16 h-16 text-slate-600" />
          </div>
        )}
      </div>
      
      {/* Vehicle Info */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide" style={{ color: config.accentColor }}>
              {vehicle.brand || config.itemLabel}
            </p>
            <h3 className="text-lg font-bold text-white mt-1">
              {vehicle.model_name}
            </h3>
            <p className="text-sm text-slate-400">
              {isGrouped
                ? (vehicle.year ? `${vehicle.year}` : "")
                : `${vehicle.version || ""}${vehicle.year ? ` • ${vehicle.year}` : ""}`}
            </p>
          </div>
        </div>
        
        {/* Price */}
        <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500">{priceLabel}</p>
            <p className={`text-xl font-bold ${isRental ? 'text-[#F09A21]' : 'text-white'}`}>{priceDisplay}</p>
          </div>
          <div className="flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all" style={{ color: config.accentColor }}>
            Ver más
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
        
        {/* Rental availability indicator */}
        {isRental && vehicle.available !== false && (
          <div className="mt-2 flex items-center gap-1 text-green-400 text-xs">
            <CheckCircle className="w-3 h-3" />
            Disponible
          </div>
        )}
      </div>
    </Link>
  );
};

import CatalogPagePremium from "./premium/CatalogPagePremium";

function CatalogPageLegacy() {
  // Get business from URL params
  const { businessSlug } = useParams();
  const staticBusiness = getBusinessFromContext(businessSlug);
  const BusinessIcon = staticBusiness.icon || Car;
  
  const [vehicles, setVehicles] = useState([]);
  const [rentalVehicles, setRentalVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Toggle between sale and rental
  const [viewMode, setViewMode] = useState("sale"); // "sale" or "rental"
  
  // Dynamic business config from API (includes phone/whatsapp from settings)
  const [business, setBusiness] = useState(staticBusiness);
  
  // Featured section state
  const [banner, setBanner] = useState(null);
  const [featuredVehicles, setFeaturedVehicles] = useState([]);
  const featuredRef = React.useRef(null);

  useEffect(() => {
    fetchVehicles();
    fetchRentalVehicles();
    fetchFeatured();
  }, [staticBusiness.id]);

  useEffect(() => {
    filterVehicles();
  }, [vehicles, rentalVehicles, selectedBrand, searchTerm, viewMode]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/public/vehicles?business_id=${staticBusiness.id}`);
      if (response.data.success) {
        setVehicles(response.data.vehicles);
        setBrands(response.data.available_brands || []);
        // Update business with API data (includes configured phone/whatsapp)
        if (response.data.business) {
          setBusiness({ ...staticBusiness, ...response.data.business });
        }
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRentalVehicles = async () => {
    try {
      const response = await axios.get(`${API}/public/rental-vehicles?business_id=${staticBusiness.id}`);
      if (response.data.success) {
        setRentalVehicles(response.data.vehicles);
      }
    } catch (error) {
      console.error("Error fetching rental vehicles:", error);
    }
  };

  const fetchFeatured = async () => {
    try {
      const response = await axios.get(`${API}/public/featured?business_id=${staticBusiness.id}`);
      if (response.data.success) {
        setBanner(response.data.banner);
        setFeaturedVehicles(response.data.featured_vehicles || []);
        // Update business with API data
        if (response.data.business) {
          setBusiness(prev => ({ ...prev, ...response.data.business }));
        }
      }
    } catch (error) {
      console.error("Error fetching featured:", error);
    }
  };

  const filterVehicles = () => {
    // Select source based on view mode
    let source = viewMode === "rental" ? rentalVehicles : vehicles;
    let filtered = [...source];
    
    if (selectedBrand) {
      filtered = filtered.filter(v => 
        v.brand?.toLowerCase() === selectedBrand.toLowerCase()
      );
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(v => 
        v.model_name?.toLowerCase().includes(term) ||
        v.brand?.toLowerCase().includes(term) ||
        v.version?.toLowerCase().includes(term)
      );
    }
    
    // Agrupar por Marca+Modelo+Año SOLO para Automotriz en modo Venta.
    // La card mostrará la versión más barata y "Precio desde".
    if (viewMode === "sale" && staticBusiness.id === "automotriz") {
      const toNum = (p) => {
        if (!p) return 0;
        const n = parseFloat(String(p).replace(/[,$]/g, ""));
        return isNaN(n) ? 0 : n;
      };
      const groups = new Map();
      for (const v of filtered) {
        const key = `${(v.brand || "").toLowerCase().trim()}|${(v.model_name || "").toLowerCase().trim()}|${(v.year || "").toString().trim()}`;
        const existing = groups.get(key);
        if (!existing) {
          groups.set(key, { base: v, count: 1 });
        } else {
          existing.count += 1;
          if (toNum(v.price) > 0 && (toNum(existing.base.price) === 0 || toNum(v.price) < toNum(existing.base.price))) {
            existing.base = v;
          }
        }
      }
      filtered = Array.from(groups.values()).map(({ base, count }) => ({
        ...base,
        _grouped: true,
        _versionsCount: count,
      }));
    }
    
    setFilteredVehicles(filtered);
  };

  const clearFilters = () => {
    setSelectedBrand("");
    setSearchTerm("");
  };

  const scrollToFeatured = () => {
    featuredRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Handler for category click - filters by search term
  const handleCategoryFilter = (filterKey) => {
    setSearchTerm(filterKey);
    setSelectedBrand("");
  };

  return (
    <div className="min-h-screen bg-[#0B1623]">
      {/* Header - Logo grande, barra compacta */}
      <header className="bg-[#0B1623] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-1">
          <div className="flex items-center justify-between">
            <Link to={`/catalogo/${business.slug}`} className="flex items-center gap-4">
              {/* Logo oficial para Maquinaria - tamaño perfecto */}
              {business.logoUrl ? (
                <img 
                  src={business.logoUrl} 
                  alt="HEBEH" 
                  className="h-16 md:h-20 w-auto object-contain"
                  data-testid="header-logo"
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${business.accentColor}, ${business.accentColorSecondary})` }}
                >
                  <BusinessIcon className="w-5 h-5 text-white" />
                </div>
              )}
            </Link>
            
            {/* Título a la derecha - texto pequeño */}
            <h1 className="font-montserrat text-sm md:text-base font-semibold text-white/80 tracking-wide">
              {business.id === "maquinaria" ? "Maquinaria y Transporte" : "AUTOMOTRIZ"}
            </h1>
          </div>
        </div>
      </header>

      {/* Hero Carousel for Maquinaria - Fuera del main para que esté pegado al header */}
      {business.heroImages && business.heroImages.length > 0 && (
        <HeroCarousel business={business} />
      )}

      <main className="max-w-7xl mx-auto px-6 pb-8">
        {/* Promotional Banner - Only show if no hero carousel */}
        {(!business.heroImages || business.heroImages.length === 0) && (
          <PromoBanner 
            banner={banner} 
            featuredCount={featuredVehicles.length}
            onScrollToFeatured={scrollToFeatured}
            business={business}
          />
        )}

        {/* Why Choose Us Section - Only for Maquinaria */}
        {business.id === "maquinaria" && (
          <WhyChooseUsSection business={business} />
        )}

        {/* Brands Section - Only for Maquinaria */}
        {business.id === "maquinaria" && (
          <BrandsSection business={business} />
        )}

        {/* Equipment Categories Section - Only for Maquinaria */}
        {business.id === "maquinaria" && (
          <EquipmentCategoriesSection business={business} onCategoryClick={handleCategoryFilter} vehicles={vehicles} />
        )}

        {/* Featured Vehicles Section */}
        {featuredVehicles.length > 0 && (
          <section ref={featuredRef} className="mb-12" data-testid="featured-section">
            <div className="flex items-center gap-3 mb-6">
              <Star className="w-6 h-6" style={{ color: business.accentColor }} />
              <h3 className="text-2xl font-bold text-white">
                {business.itemLabelPlural.charAt(0).toUpperCase() + business.itemLabelPlural.slice(1)} Destacados
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredVehicles.map((vehicle) => (
                <FeaturedVehicleCard key={vehicle.id} vehicle={vehicle} business={business} />
              ))}
            </div>
          </section>
        )}

        {/* Hero Section - Only show if no carousel */}
        {(!business.heroImages || business.heroImages.length === 0) && (
          <section className="mb-10">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
                {business.heroTitle}
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                {business.heroSubtitle}
              </p>
            </div>
          </section>
        )}

        {/* Search & Filters */}
        <section id="catalog-section" className="mb-8" data-testid="search-section">
          <div className="glass-card p-4 lg:p-6">
            {/* Sale/Rental Toggle */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <button
                onClick={() => setViewMode("sale")}
                className={`px-6 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  viewMode === "sale"
                    ? "bg-[#1E4C7A] text-white shadow-lg"
                    : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
                }`}
                data-testid="view-mode-sale"
              >
                <DollarSign className="w-4 h-4" />
                Venta
                <span className="ml-1 text-xs opacity-70">({vehicles.length})</span>
              </button>
              <button
                onClick={() => setViewMode("rental")}
                className={`px-6 py-2.5 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                  viewMode === "rental"
                    ? "bg-[#F09A21] text-white shadow-lg"
                    : "bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700"
                }`}
                data-testid="view-mode-rental"
              >
                <Truck className="w-4 h-4" />
                Renta
                <span className="ml-1 text-xs opacity-70">({rentalVehicles.length})</span>
              </button>
            </div>
            
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por marca, modelo o versión..."
                  className="w-full pl-12 pr-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition-colors"
                  data-testid="search-input"
                />
              </div>
              
              {/* Brand Filter */}
              <div className="md:w-48">
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0a1526] border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors appearance-none cursor-pointer"
                  data-testid="brand-filter"
                >
                  <option value="">Todas las marcas</option>
                  {brands.map((brand, idx) => (
                    <option key={idx} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
              
              {/* Clear Filters */}
              {(selectedBrand || searchTerm) && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-3 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:border-slate-600 transition-colors flex items-center gap-2"
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4" />
                  Limpiar
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Vehicles Grid */}
        <section className="mb-16" data-testid="vehicles-section">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-white">
              {viewMode === "rental" ? "Equipos en Renta" : `${business.itemLabelPlural.charAt(0).toUpperCase() + business.itemLabelPlural.slice(1)} disponibles`}
              <span className="ml-2 text-sm font-normal text-slate-500">
                ({filteredVehicles.length})
              </span>
            </h3>
            {viewMode === "rental" && (
              <span className="px-3 py-1 bg-[#F09A21]/20 text-[#F09A21] rounded-lg text-sm font-medium">
                Enviamos a toda la República
              </span>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: business.accentColor }} />
            </div>
          ) : filteredVehicles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredVehicles.map((vehicle) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} business={business} isRental={viewMode === "rental"} />
              ))}
            </div>
          ) : (
            <div className="glass-card p-12 text-center">
              <BusinessIcon className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <h4 className="text-xl font-semibold text-white mb-2">
                No hay {business.itemLabelPlural} disponibles
              </h4>
              <p className="text-slate-400">
                {selectedBrand || searchTerm 
                  ? `No encontramos ${business.itemLabelPlural} con esos filtros. Intenta con otros criterios.`
                  : `Pronto tendremos ${business.itemLabelPlural} disponibles en el catálogo.`
                }
              </p>
              {(selectedBrand || searchTerm) && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-6 py-2 rounded-lg text-white hover:opacity-90 transition-colors"
                  style={{ backgroundColor: business.accentColor }}
                >
                  Ver todos los {business.itemLabelPlural}
                </button>
              )}
            </div>
          )}
        </section>

        {/* Stats Section - Only for Automotriz */}
        {business.id !== "maquinaria" && (
        <section className="mb-16" data-testid="stats-section">
          <h3 className="text-2xl font-bold text-white mb-6">HEBEH en números</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Award} value="11" label="marcas aliadas" />
            <StatCard icon={Truck} value="200" suffix="+" label="vehículos entregados" />
            <StatCard icon={Users} value="90" suffix="%" label="aprobación de clientes" />
            <StatCard icon={Clock} value="10" suffix=" min" label="preaprobación" />
          </div>
        </section>
        )}

        {/* Guarantees Section - Only for Automotriz */}
        {business.id !== "maquinaria" && (
        <section className="mb-16" data-testid="guarantees-section">
          <h3 className="text-2xl font-bold text-white mb-6">Garantías de tu experiencia</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <GuaranteeCard 
              icon={Clock} 
              title="Preaprobación en 10 min" 
              description="Soft check, no afecta tu buró."
            />
            <GuaranteeCard 
              icon={DollarSign} 
              title="Sin anticipos" 
              description="Pagas el enganche en la entrega."
            />
            <GuaranteeCard 
              icon={Shield} 
              title="Precio transparente" 
              description="Todo incluido, sin sorpresas."
            />
            <GuaranteeCard 
              icon={Truck} 
              title="Entrega verificada" 
              description="En agencia o a domicilio."
            />
            <GuaranteeCard 
              icon={MessageCircle} 
              title="Asesor 1:1 por WhatsApp" 
              description="Acompañamiento durante todo el proceso."
            />
          </div>
        </section>
        )}

        {/* Steps Section - Only for Automotriz */}
        {business.id !== "maquinaria" && (
        <section className="mb-16" data-testid="steps-section">
          <h3 className="text-2xl font-bold text-white mb-6">Tu trámite en 3 pasos</h3>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <StepCard 
              number="1" 
              title="Preaprobación en 10 min" 
              description="Soft check, no afecta tu buró."
            />
            <StepCard 
              number="2" 
              title="Sube documentos" 
              description="Valida identidad y comprobantes."
            />
            <StepCard 
              number="3" 
              title="Entrega" 
              description="En agencia o a domicilio. Pagas el enganche en la entrega."
            />
          </div>
        </section>
        )}

        {/* Quote Form Section - Only for Maquinaria */}
        {/* NOTA: Esta sección se renderiza fuera del main para permitir fondo full-width */}

        {/* CTA Section */}
        <section className="mb-10" data-testid="cta-section">
          <div 
            className="glass-card p-8 text-center"
            style={{ 
              background: `linear-gradient(135deg, ${business.accentColor}10, ${business.accentColorSecondary}10)`,
              borderColor: `${business.accentColor}30`
            }}
          >
            <h3 className="text-2xl font-bold text-white mb-3">¿Listo para cotizar?</h3>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              {business.id === "maquinaria" 
                ? "Selecciona el equipo que te interesa y usa nuestro simulador de financiamiento. Sin compromisos."
                : "Selecciona el vehículo que te interesa y usa nuestro simulador de financiamiento. Sin compromisos, sin afectar tu buró."
              }
            </p>
            <a
              href={`https://api.whatsapp.com/send?phone=${business.whatsapp}&text=${encodeURIComponent(business.id === "maquinaria" ? WHATSAPP_DEFAULT_MESSAGE : `Hola, quisiera información sobre un ${business.itemLabel}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-lg bg-[#25D366] text-white font-semibold hover:bg-[#20BD5C] transition-colors"
              data-testid="cta-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Contáctanos por WhatsApp
            </a>
          </div>
        </section>
      </main>

      {/* Quote Form Section - Only for Maquinaria - Fuera del main para fondo full-width */}
      {business.id === "maquinaria" && (
        <QuoteFormSection business={business} />
      )}

      {/* Footer - Mínimo */}
      <footer className="border-t border-slate-800/50 py-4">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-xs text-slate-600">
            © 2026 HEBEH. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Floating Buttons - Todos juntos a la derecha */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50">
        {/* Scroll to top button */}
        <ScrollToTopButton />
        {/* Chatbot button - junto al WhatsApp */}
        <ChatbotButton business={business} />
        {/* WhatsApp button */}
        <WhatsAppButton business={business} />
      </div>
    </div>
  );
}


// Router: Automotriz uses the premium cinematic redesign; otras verticales (maquinaria, etc.)
// mantienen el layout heredado en CatalogPageLegacy.
export default function CatalogPage() {
  const { businessSlug } = useParams();
  const staticBusiness = getBusinessFromContext(businessSlug);
  if (staticBusiness.id === "automotriz") {
    return <CatalogPagePremium />;
  }
  return <CatalogPageLegacy />;
}
